/**
 * StepIndicator — a horizontal numbered stepper for the Onboarding Wizard.
 *
 * Replaces the Cloudscape `<Wizard>`'s left navigation rail with a compact top
 * row of numbered circles + connector lines + step titles, reclaiming the full
 * page width for the wizard's wide metric/parameter tables.
 *
 * Per-step visual state (driven entirely by props — no internal state):
 *   - completed  (index < activeStepIndex, and reachable): filled accent circle
 *                 with a check icon; CLICKABLE.
 *   - active     (index === activeStepIndex): highlighted/accent circle showing
 *                 the step number; the current step.
 *   - upcoming   (index > furthestValidatedIndex): muted outline circle; NOT
 *                 clickable.
 *
 * A step is clickable when `index <= furthestValidatedIndex`. Backward
 * navigation is always in range because the parent computes
 * `furthestValidatedIndex >= activeStepIndex`.
 *
 * Colors/spacing come from `@amzn/awsui-design-tokens` (inline style values) so
 * the stepper tracks the active light/dark theme instead of hardcoded hex.
 */

import React from 'react';
import { Icon } from '@amzn/awsui-components-react';
import * as tokens from '@amzn/awsui-design-tokens';

export interface StepIndicatorProps {
  /** Ordered step definitions; only the title is rendered here. */
  steps: { title: string }[];
  /** The step currently being shown. */
  activeStepIndex: number;
  /** Highest step index the user is allowed to click to. */
  furthestValidatedIndex: number;
  /** Fired with the target index when a reachable step is clicked. */
  onStepClick: (index: number) => void;
}

type StepState = 'completed' | 'active' | 'upcoming';

const CIRCLE_SIZE = 32;

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  activeStepIndex,
  furthestValidatedIndex,
  onStepClick,
}) => {
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    flexWrap: 'wrap',
    rowGap: tokens.spaceScaledM,
    // Reset the browser's default <ol> chrome (decimal markers, ~1em block
    // margin, ~40px inline-start padding) — it would offset the row and break
    // the horizontal flex alignment.
    listStyle: 'none',
    margin: 0,
    padding: 0,
  };

  return (
    <ol style={rowStyle} aria-label="Onboarding steps">
      {steps.map((step, index) => {
        const state: StepState =
          index === activeStepIndex
            ? 'active'
            : index < activeStepIndex
              ? 'completed'
              : 'upcoming';
        const clickable = index <= furthestValidatedIndex;
        const isLast = index === steps.length - 1;

        // Circle colors per state (design tokens → theme-aware).
        let circleBackground = 'transparent';
        let circleBorderColor = tokens.colorTextStatusInactive;
        let circleTextColor = tokens.colorTextStatusInactive;
        if (state === 'active') {
          circleBackground = tokens.colorBackgroundButtonPrimaryDefault;
          circleBorderColor = tokens.colorBackgroundButtonPrimaryDefault;
          circleTextColor = tokens.colorTextButtonPrimaryDefault;
        } else if (state === 'completed') {
          circleBackground = tokens.colorTextInteractiveDefault;
          circleBorderColor = tokens.colorTextInteractiveDefault;
          circleTextColor = tokens.colorTextButtonPrimaryDefault;
        }

        const circleStyle: React.CSSProperties = {
          boxSizing: 'border-box',
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          border: `2px solid ${circleBorderColor}`,
          background: circleBackground,
          color: circleTextColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: tokens.fontSizeBodyM,
          flexShrink: 0,
        };

        const titleStyle: React.CSSProperties = {
          marginLeft: tokens.spaceScaledS,
          fontSize: tokens.fontSizeBodyM,
          fontWeight: state === 'active' ? 700 : 400,
          color:
            state === 'upcoming'
              ? tokens.colorTextStatusInactive
              : tokens.colorTextBodyDefault,
          whiteSpace: 'nowrap',
        };

        const buttonStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: clickable ? 'pointer' : 'default',
          font: 'inherit',
          textAlign: 'left',
        };

        // Connector line filling the space between this step and the next.
        const connectorStyle: React.CSSProperties = {
          flex: 1,
          minWidth: tokens.spaceScaledL,
          height: 2,
          margin: `0 ${tokens.spaceScaledS}`,
          alignSelf: 'center',
          background:
            index < activeStepIndex
              ? tokens.colorTextInteractiveDefault
              : tokens.colorBorderDividerDefault,
        };

        return (
          <li
            key={step.title}
            style={{ display: 'flex', alignItems: 'center', flex: isLast ? '0 1 auto' : '1 1 auto' }}
          >
            <button
              type="button"
              style={buttonStyle}
              disabled={!clickable}
              aria-current={state === 'active' ? 'step' : undefined}
              onClick={() => {
                if (clickable) {
                  onStepClick(index);
                }
              }}
            >
              <span style={circleStyle} aria-hidden="true">
                {state === 'completed' ? (
                  <Icon name="status-positive" variant="inverted" />
                ) : (
                  index + 1
                )}
              </span>
              <span style={titleStyle}>
                {step.title}
              </span>
            </button>
            {!isLast && <span style={connectorStyle} aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
};

export default StepIndicator;
